import memoize from 'fast-memoize';
import { createSelector } from 'reselect';

import { LEVER_MATCH_EXPIRATION_ENABLED } from 'Lib/ConfigService/levers';
import { emptyArray, emptyObject } from 'Lib/constants/placeholder';
import { GlobalStateType } from 'Lib/reduxType';
import { CACHE_SIZE_ARRAY, createDenormalizedSelector, createKeySelector } from 'Lib/reduxUtils';
import { getLeverValue } from 'Lib/utils/getLeverValue';

import { archivedMatchArray } from 'Store/entities/archivedMatches.schema';
import { getArchivedMatchById } from 'Store/entities/archivedMatches.selectors';
import { matchArray } from 'Store/entities/matches.schema';
import { getMatchById } from 'Store/entities/matches.selectors';
import { PRODUCT_TYPE_PLATINUM } from 'Store/entities/products.schema';
import {
  getIsIdVerified,
  getIsPhotoVerified,
  getIsVerified,
} from 'Store/entities/profiles.selectors';
import { selectEntities } from 'Store/entities/selectors';
import { getOptimisticMatches } from 'Store/optimistic/matches/selectors';
import { getOptimisticMessages } from 'Store/optimistic/messages/selectors';
import { getUnmatchedIds } from 'Store/optimistic/unmatchedIds/selectors';
import { getProfileUserId } from 'Store/profile/selectors';

import isMessageReportable from 'Components/Report/helpers/isMessageReportable';

import { MATCH_FILTER_WITH_MESSAGES, MATCH_FILTER_WITHOUT_MESSAGES } from './constants';
import type { DenormalizedMatch } from './types';
import {
  getGroupedMatches,
  getMatchesWithoutArchived,
  getMatchId,
  getMatchType,
  getUsersLastMessage,
  shouldShowIndicator,
  sortAllMatchesByCreatedDate,
  sortAllMatchesByLatestAction,
  sortArchivedMatchesByArchiveDatetime,
} from './utils';

export const selectMatch = createKeySelector('match');

// Match Chat Suggestions
export const getChatSuggestions = createSelector(selectMatch, (match) => match.chatSuggestions);
export const getChatSuggestionsState = createSelector(
  selectMatch,
  (match) => match.chatSuggestions.chatSuggestionsStatus
);

// Match Expiration
export const getMatchListExpirationTooltipShown = createSelector(
  selectMatch,
  (match) => match.expiration.matchListTooltipShown
);
export const getMessageListExpirationTooltipShown = createSelector(
  selectMatch,
  (match) => match.expiration.messageListTooltipShown
);

// Matches
export const getMatchesIds = createSelector(selectMatch, (match) => match?.matches ?? emptyArray);
export const getArchivedMatchesIds = createSelector(
  selectMatch,
  (match) => match?.archivedMatches ?? emptyArray
);

const getGlobalState = (state: GlobalStateType) => state;

// matches exclude optimistic unmatch
export const getCleanMatchesIds = createSelector(
  [getMatchesIds, getUnmatchedIds],
  (matchesIds, unmatchedIds) => {
    const unmatchedIdsSet = new Set(unmatchedIds);
    if (!unmatchedIdsSet.size) {
      return matchesIds;
    }
    return matchesIds.filter((id) => !unmatchedIdsSet.has(id));
  }
);

export const hasMatches = createSelector(
  [getMatchesIds, getUnmatchedIds],
  (matchesIds, unmatchedIds) => {
    const unmatchedIdsSet = new Set(unmatchedIds);
    return matchesIds.some((id) => !unmatchedIdsSet.has(id));
  }
);

// Denormalized matches
const getMatches: (state: GlobalStateType, ...args) => DenormalizedMatch[] =
  createDenormalizedSelector(getCleanMatchesIds, selectEntities, matchArray, CACHE_SIZE_ARRAY);
// Match Expiration: get archived matches only for the /archived page
export const getArchivedMatches: (state: GlobalStateType, ...args) => DenormalizedMatch[] =
  createDenormalizedSelector(
    getArchivedMatchesIds,
    selectEntities,
    archivedMatchArray,
    CACHE_SIZE_ARRAY
  );

export const getSortedArchivedMatches = createSelector(getArchivedMatches, (denormalizedMatches) =>
  sortArchivedMatchesByArchiveDatetime(denormalizedMatches)
);

export const getDenormalizedMatchById = getMatchById;
export const getDenormalizedArchivedMatchById = getArchivedMatchById;

const getSortedMatches = createSelector(getMatches, (denormalizedMatches) =>
  sortAllMatchesByLatestAction(denormalizedMatches)
);

export const getSortedMatchesByCreatedDate = createSelector(
  getMatches,
  sortAllMatchesByCreatedDate
);

export const getMatchesWithoutMessages = createSelector(getSortedMatches, (matches) => {
  const groupedMatches = getGroupedMatches(matches);
  const isMatchExpirationEnabled = getLeverValue(LEVER_MATCH_EXPIRATION_ENABLED);
  const matchesWithoutMessages = groupedMatches[MATCH_FILTER_WITHOUT_MESSAGES] ?? emptyArray;
  if (isMatchExpirationEnabled) return getMatchesWithoutArchived(matchesWithoutMessages);
  return matchesWithoutMessages;
});

export const getMatchesWithMessages = createSelector(getSortedMatches, (matches) => {
  const groupedMatches = getGroupedMatches(matches);
  const isMatchExpirationEnabled = getLeverValue(LEVER_MATCH_EXPIRATION_ENABLED);
  const matchesWithMessages = groupedMatches[MATCH_FILTER_WITH_MESSAGES] ?? emptyArray;
  if (isMatchExpirationEnabled) return getMatchesWithoutArchived(matchesWithMessages);
  return matchesWithMessages;
});

export const getNewMatchPlusMessageCount = createSelector(
  [getMatches, getOptimisticMatches],
  (matches, optimisticMatches) => {
    const newMatchPlusMessage = matches.filter((match) => {
      if (!match) return false;
      const { _id: matchId } = match;
      const optimisticMatch = optimisticMatches[matchId];

      return shouldShowIndicator(optimisticMatch ?? match);
    });

    return newMatchPlusMessage?.length ?? 0;
  }
);

export const getNewMatchCount = createSelector(
  [getMatchesWithoutMessages, getOptimisticMatches],
  (matches, optimisticMatches) =>
    matches?.filter((match) => {
      // only count the unseen matches
      const { _id: matchId } = match;
      const optimisticMatch = optimisticMatches[matchId];
      return !(optimisticMatch?.seen?.match_seen === true || match?.seen?.match_seen === true);
    })?.length ?? 0
);

export const getNewMessagesCount = createSelector(
  [getNewMatchPlusMessageCount, getNewMatchCount],
  (allNewCounts, newMatchCounts) => allNewCounts - newMatchCounts
);

export const getMatchesInfo = createSelector(
  [getMatchesWithoutMessages, getMatchesWithMessages, getNewMatchCount, getNewMessagesCount],
  (
    withoutMessages = emptyArray,
    withMessages = emptyArray,
    newMatchesCount,
    matchesWithNewMessagesCount
  ) => ({
    withMessages,
    withoutMessages,
    newMatchesCount,
    matchesWithNewMessagesCount,
    withoutMessagesCount: withoutMessages.length,
    withMessagesCount: withMessages.length,
    totalMatchesCount: withMessages.length + withoutMessages.length,
  })
);

export const getMatchesWithMessagesIds = createSelector(getMatchesWithMessages, (matches) =>
  matches.map(getMatchId)
);

export const getMatchesWithoutMessagesIds = createSelector(getMatchesWithoutMessages, (matches) =>
  matches.map(getMatchId)
);

export const isThereNewMatchOrMessage = createSelector(
  getNewMatchPlusMessageCount,
  (count) => count > 0
);

export const getMatchMessagesById = createSelector(
  [getGlobalState, getOptimisticMessages],
  (state, optimistic) =>
    memoize((matchId) => {
      const optimisticMessages = optimistic?.[matchId];
      const match = getDenormalizedMatchById(state, matchId);
      const { messages = emptyArray } = match || emptyObject;
      if (optimisticMessages?.length) {
        return messages.concat(optimisticMessages);
      }
      return messages;
    })
);

/**
 * @function getMatchMessagesReportableById returns an immutable list of message maps
 * that meet the product requirements for a message being reportable: the
 * message must be inbound  and contain swipe note, fast chat, or standard text message content.
 * @param { string } matchId - Match ID
 * @returns { Array } reportableMessages - Messages that meet the criteria above
 */
export const getMatchMessagesReportableById = createSelector(
  [getProfileUserId, getGlobalState],
  (currentUid, state) =>
    memoize((matchId: string) => {
      const match = getDenormalizedMatchById(state, matchId);
      const { messages = emptyArray } = match || emptyObject;
      return messages.filter((msg) => isMessageReportable(currentUid, msg));
    })
);

export const getMatchShouldShowIndicator = createSelector(
  [getGlobalState, getOptimisticMatches],
  (state, optimisticMatches) =>
    memoize((matchId: string) => {
      const optimisticMatch = optimisticMatches?.[matchId];
      const match = getDenormalizedMatchById(state, matchId);
      return optimisticMatch ? shouldShowIndicator(optimisticMatch) : shouldShowIndicator(match);
    })
);

export const getUserInfoByMatchId = createSelector(getDenormalizedMatchById, (match) => {
  const info = {
    person: match?.person,
    matchType: getMatchType(match),
    lastMessage: getUsersLastMessage(match?.messages, match?.person?._id),
  };
  return info;
});

export const selectMessagesNextPageToken = createSelector(getDenormalizedMatchById, (match) => {
  const lastMessage = match.messages?.[0];
  if (!lastMessage) {
    return null;
  }
  // construct next page token
  // next page token timestamp must be earlier than message time, thus - 1
  const dt = new Date(new Date(lastMessage.sent_date).getTime() - 1);
  return btoa(dt.toISOString());
});

// Match Expiration: a match is either in entities.archivedMatches or entities.matches, but not both.
// So we need to check `expire_at` in both keys, depending on whether match is archived
export const getMatchExpirationInfo = createSelector([getGlobalState], (state) =>
  memoize((matchId) => {
    const match = getDenormalizedMatchById(state, matchId);
    const matchFromArchived = getDenormalizedArchivedMatchById(state, matchId);
    let expireAtMs = match?.expire_at;
    let archiveAtMs = match?.archive_at;

    if (!expireAtMs) {
      expireAtMs = matchFromArchived?.expire_at ?? 0;
    }
    if (!archiveAtMs) {
      archiveAtMs = matchFromArchived?.expire_at ?? 0;
    }
    return {
      expireAtMs,
      archiveAtMs,
    };
  })
);

// Match attributes utility function
export const getMatchLikedContent = (match: DenormalizedMatch) => match?.liked_content;
export const getMatchName = (match: DenormalizedMatch) => match?.person?.name;
export const getMatchGender = (match: DenormalizedMatch) => match?.person?.gender ?? 0;
export const getMatchPhotos = (match: DenormalizedMatch) => match?.person?.photos ?? emptyArray;
export const getIsMatchFromSuperlike = (match: DenormalizedMatch) => match?.is_super_like;
export const getIsMatchFromBoost = (match: DenormalizedMatch) => match?.is_boost_match;
export const getIsMatchFromSuperBoost = (match: DenormalizedMatch) => match?.is_super_boost_match;
export const getIsMatchFromPrimetimeBoost = (match: DenormalizedMatch) =>
  match?.is_primetime_boost_match;
export const getIsMatchFromSwipeNote = (match: DenormalizedMatch) => {
  const { liked_content: likedContent } = match ?? emptyObject;
  const opener = likedContent?.by_opener?.is_swipe_note;
  const closer = likedContent?.by_closer?.is_swipe_note;
  return opener || closer;
};
export const getMatchTinderUInfo = (match: DenormalizedMatch) => match?.person?.tinder_u;
export const getMatchPerson = (match: DenormalizedMatch) => match?.person;
export const getMatchPersonId = (match: DenormalizedMatch) => match?.person?._id;
export const getIsMatchVerified = (match: DenormalizedMatch) => getIsVerified(match?.person);
export const getIsMatchIdVerified = (match: DenormalizedMatch) => getIsIdVerified(match?.person);
export const getIsMatchPhotoVerified = (match: DenormalizedMatch) =>
  getIsPhotoVerified(match?.person);
export const getIsFastMatch = (match: DenormalizedMatch) => match?.is_fast_match;
export const getIsPlatinumMatch = (match: DenormalizedMatch) =>
  match?.is_fast_match && match?.subscription_tier === PRODUCT_TYPE_PLATINUM;

// Typing indicator
export const getMatchShouldShowTypingIndicator = createSelector(selectMatch, (match) =>
  memoize((matchId: string) => match.typingIndicators?.[matchId] ?? false)
);
